import { useLayoutEffect, useRef, useState } from 'react'
import type { ScrollOptions } from './use-scroll-container.context'
export { ScrollContainerContext } from './use-scroll-container.context'
export type { ScrollContainerHost } from './use-scroll-container.context'

export function useScrollContainer() {
  const headerRef = useRef<HTMLElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight)
  function onScroll(event: unknown) {
    const target = (event as { currentTarget: HTMLElement }).currentTarget
    setScrollTop(target.scrollTop)
  }
  const [isAtTop, setIsAtTop] = useState(() => window.scrollY <= 0)
  const [registry] = useState(() => new Map<string, HTMLElement>())
  const pending = useRef<{ key: string; options: ScrollOptions } | null>(null)
  useLayoutEffect(() => {
    let parent = headerRef.current?.parentElement ?? null
    while (parent && !['auto', 'scroll'].includes(getComputedStyle(parent).overflowY)) parent = parent.parentElement
    const container = parent
    const sync = () => { setIsAtTop(container ? container.scrollTop <= 0 : window.scrollY <= 0) }
    sync()
    const source = container ?? window
    source.addEventListener('scroll', sync, { passive: true })
    const resize = () => { setViewportHeight(window.innerHeight) }
    window.addEventListener('resize', resize)
    return () => { source.removeEventListener('scroll', sync); window.removeEventListener('resize', resize) }
  }, [])
  function reveal(target: unknown, options: ScrollOptions = {}) {
    if (!(target instanceof HTMLElement)) return
    target.scrollIntoView?.({ block: options.block ?? 'nearest', inline: 'nearest' })
    if (options.highlight) target.dataset.flashcardHighlight = 'true'
  }
  function scrollTo(key: string, options: ScrollOptions = {}) {
    pending.current = { key, options }
    const target = registry.get(key)
    if (target) reveal(target, options)
  }
  function register(key: string, target: unknown) {
    if (!(target instanceof HTMLElement)) { registry.delete(key); return }
    registry.set(key, target)
    if (pending.current?.key === key) reveal(target, pending.current.options)
  }
  function revealFocused() {
    const target = document.activeElement
    if (target && headerRef.current?.contains(target)) reveal(target)
  }
  return { headerRef, isAtTop, register, scrollTo, reveal, revealFocused, scrollTop, viewportHeight, onScroll }
}
